import { Injectable } from "@angular/core";
import { AuthorizationInterceptor } from "src/app/interceptors/interceptor";
import { endpointBaseGRPC } from "src/main";
import { AddressServer } from "@models/address";
import * as CheckoutService from "@proto/checkout/checkout_service";
import * as CheckoutProto from "@proto/checkout/checkout";
import * as PaymentProto from "@proto/payments/payments";
import * as TimestampProto from "@proto/google/protobuf/timestamp";
import { SharedService } from "@services/shared/shared.service";
import { withPaymentDeadline } from "src/util/payment-call";


@Injectable({
  providedIn: "root",
})
export class GrpcCheckoutService {

  sessionId: string;
  tenantId: string = "8b4a1ab4-cff8-405c-9b7d-13e6cc2a69ba";
  constructor(public sharedService: SharedService) {}
  private clientAuth = new CheckoutService.checkout.v1.CheckoutServiceClient(
    endpointBaseGRPC + "/checkout",
    undefined,
    {
      unaryInterceptors: [new AuthorizationInterceptor()],
    },
  );

  createCheckout(userId: string) {
    const request = new CheckoutService.checkout.v1.CreateCheckoutSessionRequest({
      userId,
      currency: "BRL",
    });
    return this.clientAuth.CreateCheckoutSession(request, {});
  }

  validateCoupon(code: string) {
    const request = new CheckoutService.checkout.v1.ValidateCouponRequest({
      checkoutId: this.sessionId,
      code,
    });
    return this.clientAuth.ValidateCoupon(request, {});
  }

  getCheckout(id: string = this.sessionId) {
    const request = new CheckoutService.checkout.v1.GetCheckoutSessionRequest({
      id,
    });
    return this.clientAuth.GetCheckoutSession(request, {});
  }

  customerRegister(
    document: string,
    address: AddressServer,
    stateRegistration: string = "",
    companyName: string = "",
    birthDate: string = "",
  ) {
    const customer = new CheckoutProto.checkout.v1.CustomerInfo({
      document,
      billingAddress: new CheckoutProto.checkout.v1.Address(address),
    });

    // Inscrição estadual e razão social só existem para CNPJ - os campos (proto3 optional) são
    // omitidos quando vazios, para não disparar a validação do backend em cadastros com CPF
    if (stateRegistration) {
      customer.stateRegistration = stateRegistration;
    }
    if (companyName) {
      customer.companyName = companyName;
    }

    // Data de nascimento só existe para CPF - o campo é omitido quando vazio
    const birthDateTimestamp = this.toBirthDateTimestamp(birthDate);
    if (birthDateTimestamp) {
      customer.birthDate = birthDateTimestamp;
    }

    const request = new CheckoutService.checkout.v1.WithCustomerInfoRequest({
      sessionId: this.sessionId,
      customer,
    });
    return this.clientAuth.WithCustomerInfo(request, {});
  }

  /** Converte "DD/MM/YYYY" em Timestamp (meia-noite UTC). Retorna null se vazio ou inválido. */
  private toBirthDateTimestamp(birthDate: string): TimestampProto.google.protobuf.Timestamp | null {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(birthDate || "");
    if (!match) return null;

    const [, day, month, year] = match;
    const millis = Date.UTC(Number(year), Number(month) - 1, Number(day));
    if (Number.isNaN(millis)) return null;

    return new TimestampProto.google.protobuf.Timestamp({
      seconds: Math.floor(millis / 1000),
      nanos: 0,
    });
  }

  finishCheckout(paymentMethodId: string, cardId?: string, installments: number = 1) {
    let obj;
    if (paymentMethodId === "pix") {
      obj = {
        pix: new PaymentProto.payments.v1.PIXPaymentType({}),
      };
    } else {
      obj = {
        card: new PaymentProto.payments.v1.CardPaymentType({
          id: cardId,
          installments,
        }),
      };
    }
    const paymentType: PaymentProto.payments.v1.PaymentType = new PaymentProto.payments.v1.PaymentType(obj);
    if (!this.sessionId) {
      throw new Error("Session ID not found");
    }
    if (!paymentType) {
      throw new Error("Payment type not found");
    }
    const userId = this.sharedService?.user?.sub || this.sharedService?.user?.id;
    const request = new CheckoutService.checkout.v1.FinalizeCheckoutSessionRequest({
      userId,
      sessionId: this.sessionId,
      paymentType,
    });
    return this.clientAuth.FinalizeCheckoutSession(request, withPaymentDeadline());
  }

  addItem(userId: string, itemId: string, quantity: number) {
    const request = new CheckoutService.checkout.v1.AddItemToSessionRequest({
      sessionId: this.sessionId,
      priceId: itemId,
      itemId,
      quantity,
      userId,
    });
    return this.clientAuth.AddItemToSession(request, {});
  }

  finalizeUpgradePlan(planId: string, checkoutId: string, coupon: string = "") {
    const request = new CheckoutService.checkout.v1.FinalizeUpgradePlanRequest({
      checkoutId,
      coupon,
    });
    return this.clientAuth.FinalizeUpgradePlan(request, withPaymentDeadline());
  }
}
